// apps/web/src/modules/synthesis/HistoryPanel.tsx
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getHistory,
  saveHistoryItem,
  submitHistoryAnswer,
  type HistoryData,
  type HistoryTrack,
} from "@/lib/api/endpoints/profile-interview";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import "./HistoryPanel.css";

const TALK_SEED =
  "Tell me your history and I'll build it into a unified profile. The simplest way: list each thing with its START year — e.g. “Salomon Brothers 1986”, “Stanford 1982”, “Married 2016”. Include the work you've done, schools, and life milestones; the start year is what I anchor on. You can also paste your whole LinkedIn (open your profile, Cmd/Ctrl-A, paste) and I'll pull it apart.";

// One blue family (synthesis tokens) — the glyph shape carries the meaning,
// the color stays in the module's palette.
const ICON_COLOR: Record<string, string> = {
  "●": "var(--syn-accent, #15a5e3)", // work
  "♥": "var(--syn-accent-deep, #0c7bb3)", // marriage / love
  "★": "var(--syn-accent-mid, #3b82f6)", // achievement / award
  "◎": "var(--syn-accent-light, #7cc7ea)", // born
};

// "Started Xavior" / "Founded 2718" → "Xavior" / "2718". Dates are always starts.
const cleanLabel = (s: string) =>
  s.replace(/^(started|founded|co-?founded|launched|created|joined|began)\s+/i, "").trim();

function eventIcon(label: string): string {
  const s = label.toLowerCase();
  if (/\bborn\b|birth/.test(s)) return "◎";
  if (/married|wedding|engaged/.test(s)) return "♥";
  if (/moved|relocat/.test(s)) return "◆";
  if (/phd|ph\.?d|doctorate|degree|graduat|award|distinction|honou?r/.test(s))
    return "★";
  return "●";
}

type Item = {
  id: string;
  track: HistoryTrack;
  year: number;
  icon: string;
  label: string;
  hint?: string;
};

function toItems(d: HistoryData): Item[] {
  // Moves omitted (no locations; move-like life events filtered).
  const work: Item[] = d.work.map((w) => ({
    id: w.id,
    track: "work",
    year: w.startYear,
    icon: "●",
    label: cleanLabel(w.company),
    hint: [w.title, w.summary].filter(Boolean).join(" — ") || undefined,
  }));
  const events: Item[] = d.lifeEvents
    .filter((e) => eventIcon(e.label) !== "◆")
    .map((e) => ({
      id: e.id,
      track: "lifeEvents",
      year: e.year,
      icon: eventIcon(e.label),
      label: cleanLabel(e.label),
    }));
  return [...work, ...events].sort((a, b) => b.year - a.year);
}

type Form = Record<string, string>;

export function HistoryPanel() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "history");
  const { data, isError } = useQuery({
    queryKey,
    queryFn: () => getHistory(token, ws),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });
  const [talking, setTalking] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null); // expanded row
  const [editing, setEditing] = useState(false); // open row in edit mode?
  const [form, setForm] = useState<Form>({});
  const [saving, setSaving] = useState(false);

  const close = () => {
    setOpenId(null);
    setEditing(false);
  };

  // Click away (or Escape) closes the open detail/editor.
  const openRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!openId) return;
    const onDown = (e: MouseEvent) => {
      if (openRef.current && !openRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openId]);

  // Open a row's read-only detail.
  const openRow = (it: Item) => {
    setOpenId(it.id);
    setEditing(false);
  };

  // Switch the open row into edit mode, prefilling the form from the raw entry.
  const beginEdit = (it: Item) => {
    if (!data) return;
    if (it.track === "work") {
      const w = data.work.find((x) => x.id === it.id);
      if (!w) return;
      setForm({
        company: w.company,
        title: w.title ?? "",
        startYear: String(w.startYear),
        endYear: w.endYear ? String(w.endYear) : "",
        summary: w.summary ?? "",
      });
    } else {
      const e = data.lifeEvents.find((x) => x.id === it.id);
      if (!e) return;
      setForm({ label: e.label, year: String(e.year) });
    }
    setEditing(true);
  };

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async (track: HistoryTrack, id: string) => {
    const item: Record<string, unknown> = { id };
    if (track === "work") {
      item.company = (form.company ?? "").trim();
      item.title = (form.title ?? "").trim();
      if (form.startYear) item.startYear = Number(form.startYear);
      if (form.endYear !== "") item.endYear = Number(form.endYear); // blank = leave as-is
      item.summary = (form.summary ?? "").trim(); // "" clears it
    } else {
      item.label = (form.label ?? "").trim();
      if (form.year) item.year = Number(form.year);
    }
    setSaving(true);
    try {
      queryClient.setQueryData(
        queryKey,
        await saveHistoryItem(token ?? null, ws, { track, op: "edit", item }),
      );
      close();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (track: HistoryTrack, id: string) => {
    setSaving(true);
    try {
      queryClient.setQueryData(
        queryKey,
        await saveHistoryItem(token ?? null, ws, { track, op: "remove", item: { id } }),
      );
      close();
    } finally {
      setSaving(false);
    }
  };

  if (isError && !data)
    return (
      <div className="hx-empty">
        Couldn't load history.{" "}
        <button onClick={() => void queryClient.invalidateQueries({ queryKey })}>
          Retry
        </button>
      </div>
    );
  if (!data) return <div className="hx-empty">Loading…</div>;
  const items = toItems(data);

  // Read-only detail for an opened row, with Close + Edit.
  const viewBlock = (it: Item) => {
    const actions = (
      <div className="hx-view-actions">
        <button onClick={close}>Close</button>
        <button
          className="hx-view-del"
          onClick={() => void remove(it.track, it.id)}
          disabled={saving}
        >
          Delete
        </button>
        <button className="hx-view-edit" onClick={() => beginEdit(it)}>
          Edit
        </button>
      </div>
    );
    if (it.track === "work") {
      const w = data.work.find((x) => x.id === it.id);
      if (!w) return null;
      return (
        <div className="hx-view">
          <div className="hx-view-title">{w.company}</div>
          {w.title && <div className="hx-view-line">{w.title}</div>}
          <div className="hx-view-dates">
            {w.startYear}–{w.endYear ?? "now"}
          </div>
          {w.summary && <div className="hx-view-desc">{w.summary}</div>}
          {actions}
        </div>
      );
    }
    const e = data.lifeEvents.find((x) => x.id === it.id);
    if (!e) return null;
    return (
      <div className="hx-view">
        <div className="hx-view-title">{e.label}</div>
        <div className="hx-view-dates">{e.year}</div>
        {actions}
      </div>
    );
  };

  return (
    <div className="hx-wrap">
      <div className="hx-head">
        <button
          className="people-add-btn people-talk-btn"
          onClick={() => setTalking(true)}
        >
          💬 Talk to Xavior about your history
        </button>
      </div>

      {items.length === 0 ? (
        <div className="hx-empty">
          Nothing yet — talk to Xavior (you can dump your LinkedIn there).
        </div>
      ) : (
        <div className="hx-chron">
          {items.map((it) =>
            openId === it.id && !editing ? (
              <div key={it.id} ref={openRef}>
                {viewBlock(it)}
              </div>
            ) : openId === it.id && editing ? (
              <div key={it.id} ref={openRef} className="hx-edit">
                {it.track === "work" ? (
                  <>
                    <input
                      className="hx-edit-in"
                      autoFocus
                      value={form.company ?? ""}
                      onChange={(e) => set("company", e.target.value)}
                      placeholder="Company"
                    />
                    <input
                      className="hx-edit-in"
                      value={form.title ?? ""}
                      onChange={(e) => set("title", e.target.value)}
                      placeholder="Role (e.g. Founder)"
                    />
                    <div className="hx-edit-yrs">
                      <input
                        className="hx-edit-in"
                        value={form.startYear ?? ""}
                        onChange={(e) => set("startYear", e.target.value)}
                        placeholder="Start year"
                        inputMode="numeric"
                      />
                      <input
                        className="hx-edit-in"
                        value={form.endYear ?? ""}
                        onChange={(e) => set("endYear", e.target.value)}
                        placeholder="End year (blank = now)"
                        inputMode="numeric"
                      />
                    </div>
                    <input
                      className="hx-edit-in"
                      value={form.summary ?? ""}
                      onChange={(e) => set("summary", e.target.value)}
                      placeholder="What it does (short)"
                    />
                  </>
                ) : (
                  <>
                    <input
                      className="hx-edit-in"
                      autoFocus
                      value={form.label ?? ""}
                      onChange={(e) => set("label", e.target.value)}
                      placeholder="Milestone"
                    />
                    <input
                      className="hx-edit-in"
                      value={form.year ?? ""}
                      onChange={(e) => set("year", e.target.value)}
                      placeholder="Year"
                      inputMode="numeric"
                    />
                  </>
                )}
                <div className="hx-edit-actions">
                  <button onClick={() => setEditing(false)} disabled={saving}>
                    Cancel
                  </button>
                  <button
                    className="hx-edit-del"
                    onClick={() => void remove(it.track, it.id)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                  <button
                    className="hx-edit-save"
                    onClick={() => void saveEdit(it.track, it.id)}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={it.id}
                className="hx-row"
                title={it.hint}
                role="button"
                tabIndex={0}
                onClick={() => openRow(it)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openRow(it);
                }}
              >
                <span className="hx-row-year">{it.year}</span>
                <span
                  className="hx-row-icon"
                  style={{ color: ICON_COLOR[it.icon] ?? "var(--syn-accent, #15a5e3)" }}
                >
                  {it.icon}
                </span>
                <span className="hx-row-label">{it.label}</span>
                <button
                  className="hx-row-del"
                  aria-label="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(it.track, it.id);
                  }}
                >
                  ×
                </button>
              </div>
            ),
          )}
        </div>
      )}

      {talking && (
        <InterviewPanel
          seedQuestion={TALK_SEED}
          title="Talk to Xavior about your history"
          onAnswer={submitHistoryAnswer}
          continuous
          onClose={() => setTalking(false)}
          onComplete={() => void queryClient.invalidateQueries({ queryKey })}
        />
      )}
    </div>
  );
}
